import React, { useState, useContext } from "react";
import {
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Box,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";
import BundlePublishing, {
  DEFAULT_B2C_PUBLICATIONS,
} from "../components/BundlePublishing";
import RichTextEditor from "../components/RichTextEditor";
import TagsField from "../components/TagsField";
import BundleProductSelector from "../components/BundleProductSelector";
import BundleMetafieldsSection from "../sections/product/BundleMetafieldsSection";

const STATUS_OPTIONS = ["ACTIVE", "DRAFT", "ARCHIVED"];
export const TYPE_OPTIONS = [
  { value: "FIXED", label: "Different Products (several products in the same bundle)" },
  { value: "PACK", label: "Single Product (same product, different presentations e.g. 12 Pack / 24 Pack)" },
];

const paperSx = {
  width: "100%",
  backgroundColor: "background.paper",
  mb: 2,
  p: 3,
};

export default function BundleCreator({ onClose = () => {} }) {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("FIXED");
  const [status, setStatus] = useState("ACTIVE");
  const [tags, setTags] = useState([]);
  const [discount, setDiscount] = useState("");
  const [sizeDiscounts, setSizeDiscounts] = useState({});
  const [components, setComponents] = useState([]);
  const [variantImages, setVariantImages] = useState([]);
  // Editable metafields for Different Products (FIXED); PACK inherits them.
  const [mfValues, setMfValues] = useState({ metafields: {} });
  const [publications, setPublications] = useState(DEFAULT_B2C_PUBLICATIONS);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [creating, setCreating] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleCreate = async () => {
    if (!title) {
      appContext.showMessage("Title is required", "warning");
      return;
    }
    if (!components.length) {
      appContext.showMessage("Add at least one product", "warning");
      return;
    }
    if (type === "PACK" && (components[0]?.quantityOption?.values?.length || 0) < 2) {
      appContext.showMessage(
        "A pack needs at least two sizes (e.g. 12-Pack and 24-Pack)",
        "warning"
      );
      return;
    }
    if (type === "PACK") {
      const values = components[0]?.quantityOption?.values || [];
      const allHaveDiscount = values.every(
        (v) => sizeDiscounts[v.name] !== undefined && sizeDiscounts[v.name] !== ""
      );
      if (!allHaveDiscount) {
        appContext.showMessage("Set a discount for every presentation", "warning");
        return;
      }
    } else if (discount === "" || discount === null || discount === undefined) {
      appContext.showMessage("Set the discount percentage", "warning");
      return;
    }
    try {
      setCreating(true);
      const formData = new FormData();
      formData.append("title", title);
      formData.append("status", status);
      formData.append("description", description);
      formData.append("tags", JSON.stringify(tags));
      formData.append("publications", JSON.stringify(publications));
      formData.append("components", JSON.stringify(components));
      if (type === "PACK") {
        const packDiscounts = (components[0]?.quantityOption?.values || [])
          .filter((v) => sizeDiscounts[v.name] !== undefined && sizeDiscounts[v.name] !== "")
          .map((v) => ({ match: v.name, percentage: Number(sizeDiscounts[v.name]) || 0 }));
        if (packDiscounts.length) {
          formData.append("discounts", JSON.stringify(packDiscounts));
        }
      } else if (discount !== "" && discount !== null) {
        formData.append("discounts", JSON.stringify([{ percentage: Number(discount) }]));
      }
      if (type === "FIXED" && imageFile) {
        formData.append("image", imageFile);
      }
      if (type === "PACK") {
        variantImages.forEach((vi, i) => formData.append(`variantImage_${i}`, vi.file));
        formData.append(
          "variantImageNames",
          JSON.stringify(variantImages.map((vi) => vi.name))
        );
      }
      if (type === "FIXED") {
        formData.append("metafields", JSON.stringify(mfValues.metafields || {}));
      }

      const response = await fetchWithInterceptor("/api/bundles", {
        method: "POST",
        headers: { "x-access-token": authContext.getToken() },
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Failed to create bundle");
      }
      appContext.showMessage("Bundle created successfully", "success");
      onClose();
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error creating bundle", "error");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", ml: 2, mt: 3, mb: 1 }}>
        <IconButton onClick={() => onClose()} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Create a new bundle. Set the basic details, choose the products it
          includes, the discount and image.
        </Typography>
      </Box>

      <Container sx={{ pb: 3 }} component="main" maxWidth="100%">
        {/* Details */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Bundle Details
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Title"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                label="Status"
                fullWidth
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                label="Type"
                fullWidth
                value={type}
                onChange={(e) => setType(e.target.value)}
                helperText="Set at creation, can't be changed later"
              >
                {TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TagsField
                value={tags}
                isEditingMode={true}
                disablePadding={true}
                onChange={(value) => setTags(value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Products (to be built with Fer's design) */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Products
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {type === "PACK"
              ? "Single Product: pick the base product and define the presentations (e.g. 12-Pack, 24-Pack)."
              : "Different Products: add the products and the quantity for each."}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <BundleProductSelector
              type={type}
              onChange={setComponents}
              onVariantImagesChange={setVariantImages}
            />
          </Box>
        </Paper>

        {/* Discount per variant */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Discount
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set the discount percentage for the bundle. We will add up the unit
            prices of the products included in the bundle and apply the discount
            percentage you specify here. For example, if the total price of the
            products in the bundle is $100.00 and you apply a 10% discount, the
            bundle price will be $90.00.
          </Typography>
          {type === "PACK" ? (
            (components[0]?.quantityOption?.values || []).length ? (
              components[0].quantityOption.values.map((v, idx) => (
                <Box
                  key={idx}
                  sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {v.name}
                  </Typography>
                  <TextField
                    label="Discount %"
                    type="number"
                    required
                    value={sizeDiscounts[v.name] ?? ""}
                    onChange={(e) =>
                      setSizeDiscounts((prev) => ({ ...prev, [v.name]: e.target.value }))
                    }
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ width: 320 }}
                  />
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Pick a base product and define the sizes first.
              </Typography>
            )
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 2,
                py: 1,
              }}
            >
              <TextField
                label="Discount %"
                type="number"
                required
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                inputProps={{ min: 0, max: 100 }}
                sx={{ width: 320 }}
              />
            </Box>
          )}
        </Paper>

        {/* Metafields — Different Products (FIXED) only. Single Product (PACK)
            inherits them from the base product in the backend. */}
        {type === "FIXED" && (
          <Paper sx={paperSx}>
            <BundleMetafieldsSection values={mfValues} setValues={setMfValues} />
          </Paper>
        )}

        {/* Publishing */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Publishing
          </Typography>
          <BundlePublishing
            status={status}
            publications={publications}
            onChange={setPublications}
          />
        </Paper>

        {/* Description (+ Media only for Different Products / FIXED) */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {type === "PACK" ? "Description" : "Description & Media"}
          </Typography>

          <Typography variant="body2" gutterBottom>
            Description
          </Typography>
          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.23)",
              borderRadius: 1,
              p: 2,
              mb: 3,
            }}
          >
            <RichTextEditor value={description} onChange={setDescription} />
          </Box>

          {/* Different Products (FIXED) loads ONE product-level image (composed
              from its different products). Single Product (PACK) uses variant-level
              images, one per presentation. */}
          {type === "FIXED" && (
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                bgcolor: "#f5f5f5",
                height: 300,
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {imagePreview && (
                <Box
                  sx={{
                    border: "1px solid #aaa",
                    borderRadius: 2,
                    overflow: "hidden",
                    width: 260,
                    bgcolor: "#fff",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={imagePreview}
                    alt={title}
                    style={{ width: "160px", height: "auto", display: "block", borderRadius: "8px" }}
                  />
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="contained" component="label">
                  {imagePreview ? "Change image" : "Upload image"}
                  <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                </Button>
                {imagePreview && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                  >
                    Remove image
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 1 }}>
          <Button onClick={() => onClose()} disabled={creating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? "Creating..." : "Create bundle"}
          </Button>
        </Box>
      </Container>
    </>
  );
}
